/**
 * Awards / stats strip — headline + four KPI tiles (live Google reviews
 * count + product/cultivar/delivery static stats).
 */
import { useEffect, useState } from "react";
import { Star, Leaf, Home as HomeIcon, Truck } from "lucide-react";

function Stat({ icon, value, label, sub }) {
    return (
        <div className="bg-zen-bg p-6 sm:p-8 flex flex-col gap-3">
            {icon}
            <div>
                <div className="font-serif text-4xl sm:text-5xl font-light text-zen-ink leading-none mb-2">
                    {value}
                </div>
                <div className="text-[11px] uppercase tracking-[0.22em] text-zen-ink font-medium">
                    {label}
                </div>
                <div className="text-[11px] text-zen-muted mt-1.5 leading-snug">
                    {sub}
                </div>
            </div>
        </div>
    );
}

// Live-Google-Reviews tile — fetches live rating + count from /api/reviews
// for both stores, computes a weighted average. Falls back to 4.9★ / 4,300+.
function StatLiveReviews() {
    const [data, setData] = useState(null);
    useEffect(() => {
        let cancelled = false;
        const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
        Promise.all([
            fetch(`${API}/reviews/queens-long-island`).then((r) =>
                r.ok ? r.json() : null
            ),
            fetch(`${API}/reviews/staten-island`).then((r) =>
                r.ok ? r.json() : null
            ),
        ])
            .then(([a, b]) => {
                if (cancelled) return;
                const ca = (a && a.user_rating_count) || 0;
                const cb = (b && b.user_rating_count) || 0;
                const ra = (a && a.rating) || 4.9;
                const rb = (b && b.rating) || 4.9;
                const total = ca + cb;
                const wAvg =
                    total > 0 ? (ra * ca + rb * cb) / total : (ra + rb) / 2;
                Promise.resolve().then(() => setData({ total, avg: wAvg }));
            })
            .catch(() => {});
        return () => {
            cancelled = true;
        };
    }, []);
    const total = data?.total ?? 4380;
    const avg = data?.avg ?? 4.9;
    return (
        <div
            className="bg-zen-bg p-6 sm:p-8 flex flex-col gap-3"
            data-testid="stat-live-reviews"
        >
            <Star size={20} className="text-zen-olive" />
            <div>
                <div className="font-serif text-4xl sm:text-5xl font-light text-zen-ink leading-none mb-2">
                    {Number(avg).toFixed(1)}★
                </div>
                <div className="text-[11px] uppercase tracking-[0.22em] text-zen-ink font-medium">
                    {Number(total).toLocaleString()}+ Google reviews
                </div>
                <div className="text-[11px] text-zen-muted mt-1.5 leading-snug">
                    Live · Across both NY flagships
                </div>
            </div>
        </div>
    );
}

export default function HomeAwards({ liveAggCount }) {
    return (
        <section
            data-testid="awards-section"
            className="relative px-6 sm:px-10 py-10 sm:py-14 bg-zen-bg border-b border-zen-ink/8"
        >
            <div className="max-w-[1400px] mx-auto">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-8">
                    <div className="lg:col-span-7">
                        <div className="inline-flex items-center gap-2.5 px-4 py-2 mb-6 bg-zen-olive/10 border border-zen-olive/30 text-zen-olive text-[10px] uppercase tracking-[0.28em]">
                            <Star size={12} fill="#6B7C4F" className="text-zen-olive" />
                            #1 Rated NY Dispensary · Queens, LI, Staten Island
                        </div>
                        <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-light text-zen-ink leading-[1.05] max-w-3xl">
                            The{" "}
                            <span className="italic text-zen-olive">#1 rated</span>{" "}
                            cannabis dispensaries in New York.
                        </h2>
                    </div>
                    <div className="lg:col-span-5 self-end">
                        <p className="text-zen-ink-soft leading-relaxed text-base">
                            Two NY OCM-licensed flagships, family-owned, stocked
                            with one of the largest selections of legal cannabis
                            in the state. Together: 80+ neighborhoods served,{" "}
                            {Number(liveAggCount || 4380).toLocaleString()}+
                            verified Google reviews, and same-day delivery
                            across NY & select NJ.
                        </p>
                    </div>
                </div>

                <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-zen-ink/8 border border-zen-ink/8">
                    <StatLiveReviews />
                    <Stat
                        icon={<Leaf size={20} className="text-zen-olive" />}
                        value="2,800+"
                        label="Products in stock daily"
                        sub="Among NY's largest selections"
                    />
                    <Stat
                        icon={<HomeIcon size={20} className="text-zen-olive" />}
                        value="109+"
                        label="Curated cultivars"
                        sub="Rotating weekly · Lab-tested"
                    />
                    <Stat
                        icon={<Truck size={20} className="text-zen-olive" />}
                        value="Free"
                        label="Same-day delivery on $100+"
                        sub="80+ neighborhoods served"
                    />
                </div>
            </div>
        </section>
    );
}
